import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useScheduleProfiles,
  useScraperBackoffState,
  useSetScraperSchedule,
  useSetScraperBackoffOverride,
  useAcknowledgeError,
} from '../hooks/queries/useAdmin';
import { ScraperSchedule } from '../api/admin';

function formatDelay(ms: number): string {
  if (ms <= 0) return 'none';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  return `${(minutes / 60).toFixed(minutes % 60 === 0 ? 0 : 1)}h`;
}

interface Props {
  searchKey: string;
  schedule?: ScraperSchedule;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

/**
 * Replaces the old activity-cooldown badge/popover (removed as part of
 * configurable-scraper-scheduling - activity cooldown no longer exists).
 * Shows the scraper's assigned schedule profile + interval, and its live
 * backoff state, with the ability to edit both. Data is fetched on-demand
 * (only while the popover is open) since there is no bulk endpoint for it.
 */
export const ScraperScheduleBadge: React.FC<Props> = ({ searchKey, schedule, isOpen, onToggle, onClose }) => {
  const { data: profiles = [], isLoading: isLoadingProfiles } = useScheduleProfiles();
  const { data: backoff, isLoading: isLoadingBackoff } = useScraperBackoffState(searchKey, isOpen);
  const setSchedule = useSetScraperSchedule();
  const setOverride = useSetScraperBackoffOverride();
  const acknowledgeError = useAcknowledgeError();

  const [profileId, setProfileId] = useState('');
  const [intervalMinutes, setIntervalMinutes] = useState('1');
  const [overrideMinutes, setOverrideMinutes] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLSpanElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, openAbove: false });
  const isLoading = isLoadingProfiles || isLoadingBackoff;

  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current || !popoverRef.current) return;
    const updatePosition = () => {
      const rect = buttonRef.current!.getBoundingClientRect();
      const { width, height } = popoverRef.current!.getBoundingClientRect();
      const openAbove = window.innerHeight - rect.bottom < height + 8;
      setPopoverPosition({
        top: openAbove ? rect.top : rect.bottom,
        // Align to the right edge when the action column is near the viewport edge.
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        openAbove,
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen && schedule) {
      setProfileId(schedule.profileId);
      setIntervalMinutes(String(schedule.regularIntervalMinutes));
    }
  }, [isOpen, schedule]);

  React.useEffect(() => {
    if (backoff?.backoffOverride?.delayMinutes !== undefined) {
      setOverrideMinutes(String(backoff.backoffOverride.delayMinutes));
    } else {
      setOverrideMinutes('');
    }
  }, [backoff]);

  const badgeLabel = schedule ? `${schedule.regularIntervalMinutes}m` : '⚙';

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <button
        ref={buttonRef}
        className="scraper-schedule-badge scraper-schedule-badge--clickable"
        style={{ color: 'rgb(var(--c-brand))', backgroundColor: 'rgb(var(--c-brand) / 0.1)' }}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        title="Schedule & backoff"
      >
        ⏱ {badgeLabel}
      </button>
      {isOpen && (
        createPortal(<span
          className={`scraper-schedule-popover${popoverPosition.openAbove ? ' scraper-schedule-popover--above' : ''}`}
          ref={popoverRef}
          style={{ top: popoverPosition.top, left: popoverPosition.left }}
          role="dialog"
          aria-label="Schedule und Backoff"
          aria-busy={isLoading}
          onClick={(e) => e.stopPropagation()}
        >
          {isLoading && <span className="scraper-schedule-popover-info">Lade aktuelle Konfiguration...</span>}

          <label className="scraper-schedule-popover-label">Profile</label>
          <select
            className="scraper-schedule-popover-select"
            value={profileId}
            disabled={isLoading}
            onChange={(e) => setProfileId(e.target.value)}
          >
            <option value="" disabled>select…</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <label className="scraper-schedule-popover-label">Interval (min)</label>
          <input
            className="scraper-schedule-popover-input"
            type="number"
            min={1}
            max={60}
            value={intervalMinutes}
            disabled={isLoading}
            onChange={(e) => setIntervalMinutes(e.target.value)}
          />

          <button
            className="scraper-schedule-popover-btn"
            disabled={isLoading || !profileId || setSchedule.isPending}
            onClick={() => {
              const minutes = Number(intervalMinutes);
              if (!profileId || !Number.isInteger(minutes) || minutes < 1 || minutes > 60) return;
              setSchedule.mutate({ searchKey, profileId, regularIntervalMinutes: minutes });
            }}
          >
            Save schedule
          </button>

          <div className="scraper-schedule-popover-divider" />

          <span className="scraper-schedule-popover-info">
            Errors: {backoff?.errorCount ?? '–'} · Backoff: {backoff ? formatDelay(backoff.effectiveBackoffDelayMs) : '–'}
          </span>

          <label className="scraper-schedule-popover-label">Override delay (min)</label>
          <input
            className="scraper-schedule-popover-input"
            type="number"
            min={0}
            placeholder="none"
            value={overrideMinutes}
            disabled={isLoading}
            onChange={(e) => setOverrideMinutes(e.target.value)}
          />

          <button
            className="scraper-schedule-popover-btn"
            disabled={isLoading || setOverride.isPending}
            onClick={() => {
              const minutes = overrideMinutes === '' ? undefined : Number(overrideMinutes);
              setOverride.mutate({ searchKey, override: minutes === undefined ? null : { delayMinutes: minutes } });
            }}
          >
            {overrideMinutes === '' ? 'Clear override' : 'Set override'}
          </button>

          <button
            className="scraper-schedule-popover-btn"
            disabled={isLoading || !backoff?.errorCount || acknowledgeError.isPending}
            title="Reset error count to 0 (acknowledge)"
            onClick={() => {
              acknowledgeError.mutate(searchKey);
              setOverrideMinutes('');
            }}
          >
            Ack / Reset errors
          </button>

          <button className="scraper-schedule-popover-btn" onClick={onClose}>Close</button>
        </span>, document.body)
      )}
    </span>
  );
};
